import { Box, Stack, Tab, Tabs, useTheme } from "@mui/material";
import { useTabScopedSearchParams } from "hooks/useTabScopedSearchParams";
import { ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

function allyProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`
	};
}

export type NavigationTab = {
	label: string;
	TabComponent: ReactNode;
	disabled?: boolean | undefined;
	hidden?: boolean;
};

type ObjectPageTabPanelProps = {
	tabs: NavigationTab[];
	defaultTab?: string;
	ignoreSearchParams?: boolean;
	rightContent?: ReactNode;
	/**
	 * Query params that are scoped to the active tab: remembered per tab and swapped in/out
	 * of the URL on tab change, so sibling tabs can reuse generic param names (e.g. "status",
	 * "search") without leaking filters across tabs.
	 */
	tabScopedParams?: string[];
};

export function NavigationTabs({
	tabs,
	defaultTab,
	ignoreSearchParams = false,
	rightContent,
	tabScopedParams
}: ObjectPageTabPanelProps) {
	const theme = useTheme();
	const [tabValue, setTabValue] = useState(0);
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTabs = tabs.filter((tab) => !tab.hidden);
	const { switchTab } = useTabScopedSearchParams(tabScopedParams ?? []);

	// Initialize search params if not set
	useEffect(() => {
		if (ignoreSearchParams) {
			return;
		}

		const currentTab = searchParams.get("tab");

		if (!currentTab && activeTabs.length > 0) {
			const label = defaultTab ?? activeTabs[0].label;
			setSearchParams(
				(prev) => {
					const params = new URLSearchParams(prev);
					params.set("tab", label);
					return params;
				},
				{ replace: true }
			);
		}
	}, [activeTabs, defaultTab, ignoreSearchParams, searchParams, setSearchParams]);

	// Sync tabValue when search params change
	useEffect(() => {
		if (ignoreSearchParams) {
			return;
		}

		const currentTab = searchParams.get("tab");
		if (currentTab) {
			const tabIndex = activeTabs.findIndex((tab) => tab.label === currentTab);
			if (tabIndex !== -1) {
				// eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing URL param to state
				setTabValue(tabIndex);
			}
		}
	}, [searchParams, activeTabs, ignoreSearchParams]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		if (ignoreSearchParams) {
			setTabValue(newValue);
			return;
		}

		const toLabel = activeTabs[newValue].label;

		if (tabScopedParams && tabScopedParams.length > 0) {
			const fromLabel = activeTabs[tabValue]?.label ?? toLabel;
			switchTab(fromLabel, toLabel);
			return;
		}

		setSearchParams(
			(prev) => {
				const params = new URLSearchParams(prev);
				params.set("tab", toLabel);
				return params;
			},
			{ replace: true }
		);
	};

	return (
		<Stack sx={{ flex: 1, minHeight: 0 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: { xs: 1, md: 2 },
					backgroundColor: theme.palette.background.paper,
					px: { xs: 2, md: 3 },
					borderBottom: `1px solid ${theme.palette.divider}`,
					boxSizing: "border-box"
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
					<Tabs
						value={tabValue}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto"
						allowScrollButtonsMobile
						sx={{
							minHeight: { xs: 50, sm: 62, md: 62 },
							"& .MuiTabs-list": { gap: { xs: 1, md: 1.5 } },
							"& .MuiTabs-indicator": {
								backgroundColor: theme.palette.text.primary,
								height: { xs: 2, md: 3, lg: 4 }
							},
							"& .MuiTab-root": {
								backgroundColor: "transparent !important",
								color: `${theme.palette.text.secondary} !important`
							},
							"& .Mui-selected": { color: `${theme.palette.text.primary} !important` }
						}}
					>
						{activeTabs.map(({ label, disabled }, index) => (
							<Tab
								label={label}
								{...allyProps(index)}
								key={`tab-${label}`}
								disabled={disabled}
								disableRipple
								sx={{
									textTransform: "none",
									fontSize: { xs: "14px", sm: "19px", md: "19px" },
									minHeight: { xs: 50, sm: 62, md: 62 },
									px: { xs: 1.5, md: 3 },
									fontWeight: tabValue === index ? 700 : 500
								}}
							/>
						))}
					</Tabs>
				</Box>
				{rightContent && (
					<Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{rightContent}</Box>
				)}
			</Box>

			{activeTabs.map(({ TabComponent, label }, index) => (
				<Box
					role="tabpanel"
					hidden={tabValue !== index}
					id={`simple-tabpanel-${index}`}
					aria-labelledby={`simple-tab-${index}`}
					key={`tabpanel-${label}`}
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						display: tabValue === index ? "flex" : "none",
						flexDirection: "column"
					}}
				>
					{TabComponent}
				</Box>
			))}
		</Stack>
	);
}
